import { keyframes } from "@emotion/react";
import MDBox from "components/MDBox";

/**
 * Animações do aviso de sistema fora do ar (pedido da Aline em 26/10/2026): desenhadas em SVG, sem imagem externa, nas
 * cores do tema. Quem pede menos movimento no sistema operacional (prefers-reduced-motion) vê o desenho parado.
 */

const girar = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const girarAoContrario = keyframes`from { transform: rotate(0deg); } to { transform: rotate(-360deg); }`;
const sinal = keyframes`0%, 100% { opacity: 0.18; } 35%, 60% { opacity: 1; }`;
const pulsar = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); }`;

const SEM_MOVIMENTO = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

/**
 * Contorno de uma engrenagem com N dentes (trapézios), com o furo do meio (fill-rule evenodd). `fase` = ângulo, em graus,
 * do centro do 1º dente.
 */
function engrenagem(cx, cy, raioExterno, raioInterno, dentes, fase, furo) {
  const passo = (2 * Math.PI) / dentes;
  const meiaBase = passo * 0.3;
  const meiaPonta = passo * 0.17;
  const ponto = (raio, angulo) => `${(cx + raio * Math.cos(angulo)).toFixed(2)},${(cy + raio * Math.sin(angulo)).toFixed(2)}`;
  const pontos = [];
  for (let i = 0; i < dentes; i += 1) {
    const a = (fase * Math.PI) / 180 + i * passo;
    pontos.push(ponto(raioInterno, a - meiaBase), ponto(raioExterno, a - meiaPonta), ponto(raioExterno, a + meiaPonta), ponto(raioInterno, a + meiaBase));
  }
  const circulo = `M ${cx - furo},${cy} a ${furo},${furo} 0 1,0 ${furo * 2},0 a ${furo},${furo} 0 1,0 ${-furo * 2},0 Z`;
  return `M ${pontos.join(" L ")} Z ${circulo}`;
}

// Duas engrenagens encaixadas: mesmo passo (raio primitivo proporcional aos dentes) e velocidade inversa ao nº de dentes
const GRANDE = { cx: 67, cy: 66, dentes: 12, raio: 27 };
const PEQUENA = { dentes: 8, raio: 18 };
const ANGULO = (-38 * Math.PI) / 180;
PEQUENA.cx = GRANDE.cx + (GRANDE.raio + PEQUENA.raio) * Math.cos(ANGULO);
PEQUENA.cy = GRANDE.cy + (GRANDE.raio + PEQUENA.raio) * Math.sin(ANGULO);
const VOLTA_GRANDE_S = 9;
const VOLTA_PEQUENA_S = (VOLTA_GRANDE_S * PEQUENA.dentes) / GRANDE.dentes;

const CAMINHO_GRANDE = engrenagem(GRANDE.cx, GRANDE.cy, GRANDE.raio + 4, GRANDE.raio - 4, GRANDE.dentes, -38, 9);
// A pequena começa com um vão de frente para o dente da grande
const CAMINHO_PEQUENA = engrenagem(PEQUENA.cx, PEQUENA.cy, PEQUENA.raio + 4, PEQUENA.raio - 4, PEQUENA.dentes,
  -38 + 180 + 180 / PEQUENA.dentes, 6);

/** Manutenção: duas engrenagens girando, encaixadas. */
export function AnimacaoManutencao({ cor, corApoio }) {
  return (
    <MDBox component="svg" viewBox="0 0 160 110" width={192} height={132} aria-hidden="true" sx={{ display: "block", mx: "auto" }}>
      <MDBox
        component="path"
        d={CAMINHO_GRANDE}
        fillRule="evenodd"
        sx={{
          fill: cor,
          transformBox: "view-box",
          transformOrigin: `${GRANDE.cx}px ${GRANDE.cy}px`,
          animation: `${girar} ${VOLTA_GRANDE_S}s linear infinite`,
          ...SEM_MOVIMENTO,
        }}
      />
      <MDBox
        component="path"
        d={CAMINHO_PEQUENA}
        fillRule="evenodd"
        sx={{
          fill: corApoio,
          transformBox: "view-box",
          transformOrigin: `${PEQUENA.cx.toFixed(2)}px ${PEQUENA.cy.toFixed(2)}px`,
          animation: `${girarAoContrario} ${VOLTA_PEQUENA_S}s linear infinite`,
          ...SEM_MOVIMENTO,
        }}
      />
    </MDBox>
  );
}

// Arcos do sinal (de dentro para fora), abertos para cima a partir do ponto (70, 92)
const CENTRO = { x: 70, y: 92 };
const arco = (raio) => {
  const d = raio * Math.SQRT1_2;
  return `M ${CENTRO.x - d},${CENTRO.y - d} A ${raio},${raio} 0 0,1 ${CENTRO.x + d},${CENTRO.y - d}`;
};
const ARCOS = [20, 38, 56];

/**
 * Sem conexão: o sinal do Wi-Fi acendendo de dentro para fora (procurando) e o sinal de "sem conexão" pulsando. O "x" do
 * sinal vai na cor do fundo do aviso (`corFundo`), como um recorte: lê bem no claro (vermelho) e no escuro (rosa claro).
 */
export function AnimacaoSemConexao({ cor, corAlerta, corFundo }) {
  return (
    <MDBox component="svg" viewBox="0 0 160 110" width={192} height={132} aria-hidden="true" sx={{ display: "block", mx: "auto" }}>
      <MDBox component="circle" cx={CENTRO.x} cy={CENTRO.y} r={6} sx={{ fill: cor }} />
      {ARCOS.map((raio, i) => (
        <MDBox
          key={raio}
          component="path"
          d={arco(raio)}
          sx={{
            fill: "none",
            stroke: cor,
            strokeWidth: 9,
            strokeLinecap: "round",
            animation: `${sinal} 2.4s ease-in-out ${i * 0.35}s infinite`,
            opacity: 0.18,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.6 },
          }}
        />
      ))}
      <MDBox
        component="g"
        sx={{
          transformBox: "view-box",
          transformOrigin: "124px 24px",
          animation: `${pulsar} 1.6s ease-in-out infinite`,
          ...SEM_MOVIMENTO,
        }}
      >
        <MDBox component="circle" cx={124} cy={24} r={15} sx={{ fill: corAlerta }} />
        <MDBox component="path" d="M 118,18 L 130,30 M 130,18 L 118,30" sx={{ fill: "none", stroke: corFundo, strokeWidth: 4, strokeLinecap: "round" }} />
      </MDBox>
    </MDBox>
  );
}
